import React, { useMemo, useRef, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import RouterIcon from '@mui/icons-material/Router';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

import { BitacoraCliente } from './MigracionOltBitacora';

import {
  useMigracionOltHistorial,
  useMigracionOltHistorialDetalle,
  useMigracionOltEjecuciones,
  useMigracionOltPendientes,
  useMigracionOltReintentarActivacion,
  type IMigracionOltHistorialItem,
  type IMigracionOltPasoHistorial,
  type IMigracionOltResultadoMigracion,
} from '@/shared/actions/erp/tickets-soporte-cliente/softnet-import.actions';

/**
 * Subtab "Historial" de la migración por OLT.
 *
 * Qué resuelve: los pasos 1 a 3 son de solo lectura y no dejaban rastro. Si una
 * corrida salía raro no había forma de volver a ver qué contestó Softnet o la
 * OLT en ese momento, y esas respuestas cambian con el tiempo. Acá queda cada
 * consulta con su body, su respuesta completa y lo que tardó.
 *
 * Se lee, no se edita: es una traza de auditoría.
 */

const PASO_CFG: Record<
  IMigracionOltPasoHistorial,
  { label: string; detalle: string; color: 'default' | 'info' | 'success' | 'warning' | 'error' }
> = {
  CONSULTAR_ONT: {
    label: 'Paso 1 · Consultar ONT',
    detalle: 'auditoriaConsultaOntConfigurado — SNMP walk de solo lectura sobre la OLT',
    color: 'info',
  },
  CONSULTAR_SOFTNET: {
    label: 'Paso 2 · Softnet',
    detalle: 'obtenerContratos, una petición por serie',
    color: 'info',
  },
  SERVICE_PROFILE: {
    label: 'Paso 3 · Service profile',
    detalle: 'Cruce contra el inventario del ERP (api_series → api_producto)',
    color: 'info',
  },
  EXCEL: {
    label: 'Excel de perfiles',
    detalle: 'Lectura del archivo que prepara el área técnica',
    color: 'default',
  },
  CONFIGURACION_ONT: {
    label: 'Configuración de ONT',
    detalle: 'cancelarSinValidacion con cancelar_servicios: false — solo consulta',
    color: 'info',
  },
  PREPARAR: {
    label: 'Paso 4 · Revisión previa',
    detalle: 'Arma y clasifica los payloads. No ejecuta nada',
    color: 'warning',
  },
  MIGRAR: {
    label: 'Migración de un cliente',
    detalle: 'Las tres llamadas: consultar, desconfigurar y activar',
    color: 'error',
  },
  REINTENTO_ACTIVACION: {
    label: 'Reintento de activación',
    detalle: 'Repite SOLO la activación; no vuelve a desconfigurar nada',
    color: 'warning',
  },
};

const seg = (ms?: number | null) =>
  ms === null || ms === undefined ? '—' : `${(ms / 1000).toFixed(1)}s`;

/** Solo la hora: la fecha ya la da la cabecera del día. */
const hora = (iso: string) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleTimeString('es-EC', { hour: '2-digit', minute: '2-digit' });
};

const fecha = (iso: string) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString('es-EC');
};

/** Bloque de JSON crudo, monoespaciado y con scroll. */
const BloqueJson: React.FC<{ titulo: string; valor: unknown }> = ({ titulo, valor }) => (
  <Box sx={{ mb: 1 }}>
    <Typography
      variant="caption"
      sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase' }}
    >
      {titulo}
    </Typography>
    <Box
      component="pre"
      sx={{
        m: 0,
        mt: 0.5,
        p: 1.25,
        maxHeight: 420,
        overflow: 'auto',
        fontFamily: 'monospace',
        fontSize: 12,
        lineHeight: 1.5,
        borderRadius: 1,
        bgcolor: 'action.hover',
        border: '1px solid',
        borderColor: 'divider',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
      }}
    >
      {valor === null || valor === undefined
        ? '(sin contenido)'
        : JSON.stringify(valor, null, 2)}
    </Box>
  </Box>
);

/** Una entrada: cabecera clicable y, al abrirla, el request y el response. */
const kb = (b?: number | null) =>
  b === null || b === undefined ? null : `${Math.max(1, Math.round(b / 1024))} KB`;

/**
 * Contadores que vale la pena ver sin abrir la entrada, por paso.
 *
 * Antes se pintaba un chip por cada clave del resumen: la consulta a Softnet
 * trae 7 (solicitadas, consultadas, encontrados, sin_datos, no_existen,
 * errores, truncado) y la fila quedaba ilegible. Acá van solo los que informan
 * del resultado, y los que valen cero se omiten salvo el principal.
 */
const CONTADORES_CLAVE: Record<string, { claves: string[]; siempre: string[] }> = {
  CONSULTAR_ONT: { claves: ['total_ONTs'], siempre: ['total_ONTs'] },
  CONSULTAR_SOFTNET: {
    claves: ['encontrados', 'no_existen', 'errores', 'truncado'],
    siempre: ['encontrados'],
  },
  SERVICE_PROFILE: {
    claves: ['con_service_profile', 'sin_service_profile', 'discrepancias'],
    siempre: ['con_service_profile'],
  },
  EXCEL: { claves: ['perfiles', 'duplicados', 'upgrades'], siempre: ['perfiles'] },
  CONFIGURACION_ONT: { claves: ['con_datos', 'errores'], siempre: ['con_datos'] },
  PREPARAR: {
    claves: ['listos', 'ya_migrados', 'no_es_home', 'faltan_datos'],
    siempre: ['listos'],
  },
  MIGRAR: {
    claves: ['estado_final', 'aprovisionamiento_ok', 'reintentos_activacion'],
    siempre: ['estado_final'],
  },
  REINTENTO_ACTIVACION: {
    claves: ['estado_final', 'activar_ok', 'reintentos_activacion'],
    siempre: ['estado_final'],
  },
};

const contadoresVisibles = (
  paso: string,
  resumen: Record<string, unknown> | null,
): [string, string][] => {
  if (!resumen) return [];
  const cfg = CONTADORES_CLAVE[paso];
  const claves = cfg ? cfg.claves : Object.keys(resumen);
  const siempre = cfg ? cfg.siempre : [];
  const out: [string, string][] = [];
  for (const k of claves) {
    const v = resumen[k];
    if (v === null || v === undefined || v === '') continue;
    // Un cero o un false no aporta nada salvo que sea el contador principal.
    if (!siempre.includes(k) && (v === 0 || v === false)) continue;
    out.push([k.replace(/_/g, ' '), String(v)]);
  }
  return out;
};

/**
 * Una entrada del historial: una línea compacta y, al abrirla, el request y el
 * response completos.
 *
 * El detalle se pide SOLO al abrir. El listado no lo trae porque es grande (el
 * de un lote de 20 series de Softnet pesa 1,2 MB); traerlo para las 50 filas
 * serían ~60 MB en una respuesta.
 */
const Entrada: React.FC<{ item: IMigracionOltHistorialItem }> = ({ item }) => {
  const [abierto, setAbierto] = useState(false);
  const cfg = PASO_CFG[item.paso] ?? {
    label: item.paso,
    detalle: '',
    color: 'default' as const,
  };

  // enabled solo cuando está abierta; staleTime infinito, así que reabrir no
  // vuelve a pedirlo.
  const { data, isFetching, isError } = useMigracionOltHistorialDetalle(
    item.id_historial,
    abierto,
  );
  const detalle = (data as { data?: { detalle?: { request?: unknown; response?: unknown } } })
    ?.data?.detalle;

  const contadores = contadoresVisibles(item.paso, item.resumen);

  return (
    <Box
      sx={{
        borderTop: '1px solid',
        borderColor: 'divider',
        '&:first-of-type': { borderTop: 'none' },
      }}
    >
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{
          px: 1.5,
          py: 0.75,
          cursor: 'pointer',
          '&:hover': { bgcolor: 'action.hover' },
        }}
        onClick={() => setAbierto((v) => !v)}
      >
        {/* Semáforo fino en vez de un chip: el estado se lee de un vistazo sin
            robarle espacio al resto de la línea. */}
        <Box
          sx={{
            width: 6,
            height: 6,
            borderRadius: '50%',
            flexShrink: 0,
            bgcolor: item.ok ? 'success.main' : 'error.main',
          }}
        />
        <Tooltip arrow title={cfg.detalle}>
          <Typography variant="body2" sx={{ minWidth: 168, fontWeight: 600 }}>
            {cfg.label}
          </Typography>
        </Tooltip>

        {item.etiqueta && (
          <Typography
            variant="caption"
            sx={{ fontFamily: 'monospace', color: 'text.secondary', minWidth: 110 }}
          >
            {item.etiqueta}
          </Typography>
        )}

        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ flexGrow: 1 }}>
          {contadores.map(([k, v]) => (
            <Chip key={k} size="small" variant="outlined" label={`${k}: ${v}`} />
          ))}
        </Stack>

        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
          {hora(item.fecha_creacion)} · {seg(item.duracion_ms)}
        </Typography>
        <Button size="small" sx={{ textTransform: 'none', minWidth: 0 }}>
          {abierto ? 'Ocultar' : `JSON${kb(item.detalle_bytes) ? ` ${kb(item.detalle_bytes)}` : ''}`}
        </Button>
      </Stack>

      {abierto && (
        <Box sx={{ px: 1.5, pb: 1.5, bgcolor: 'background.default' }}>
          {item.mensaje && item.mensaje !== 'null' && (
            <Alert
              severity={item.ok ? 'info' : 'error'}
              variant="outlined"
              sx={{ mb: 1.5, py: 0.5 }}
            >
              {item.mensaje}
            </Alert>
          )}

          {/* Al abrir sí se muestra el resumen completo, no solo lo curado. */}
          {item.resumen && Object.keys(item.resumen).length > 0 && (
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
              {Object.entries(item.resumen)
                .filter(([, v]) => v !== null && v !== undefined && v !== '')
                .map(([k, v]) => (
                  <Chip
                    key={k}
                    size="small"
                    variant="outlined"
                    label={`${k.replace(/_/g, ' ')}: ${String(v)}`}
                  />
                ))}
            </Stack>
          )}

          {isFetching && (
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 2 }}>
              <CircularProgress size={18} />
              <Typography variant="body2" color="text.secondary">
                Trayendo el detalle
                {kb(item.detalle_bytes) ? ` (${kb(item.detalle_bytes)})` : ''}...
              </Typography>
            </Stack>
          )}

          {isError && (
            <Alert severity="error" variant="outlined" sx={{ py: 0.5 }}>
              No se pudo traer el detalle de esta entrada.
            </Alert>
          )}

          {!isFetching && !isError && (
            <>
              <BloqueJson titulo="Body enviado" valor={detalle?.request} />
              <BloqueJson titulo="Respuesta completa" valor={detalle?.response} />
              <Typography variant="caption" color="text.secondary">
                La clave PPPoE se guarda enmascarada; nunca llega en claro a esta tabla.
              </Typography>
            </>
          )}
        </Box>
      )}
    </Box>
  );
};

/**
 * Un lote de trabajo: todos los pasos que se hicieron en la misma corrida.
 *
 * Agrupar importa porque los pasos sueltos no dicen nada: lo que se revisa es
 * "qué se hizo sobre la OLT X con el archivo Y". De cada lote se muestran los
 * datos lógicos — IP, nombre de la OLT y nombre del archivo — nunca el archivo
 * físico, que no se guarda.
 */
interface Lote {
  clave: string;
  corridaId: string | null;
  ipOlt: string | null;
  nombreOlt: string | null;
  archivo: string | null;
  desde: string;
  hasta: string;
  usuario: string | null;
  duracionTotalMs: number;
  conFallo: boolean;
  entradas: IMigracionOltHistorialItem[];
}

const agruparEnLotes = (items: IMigracionOltHistorialItem[]): Lote[] => {
  const mapa = new Map<string, Lote>();
  for (const h of items) {
    // Las entradas viejas no tienen corrida: cada una va en su propio lote en
    // vez de amontonarlas en un "sin corrida" que mezclaría OLT distintas.
    const clave = h.corrida_id ?? `suelta:${h.id_historial}`;
    const lote = mapa.get(clave);
    if (!lote) {
      mapa.set(clave, {
        clave,
        corridaId: h.corrida_id,
        ipOlt: h.ip_olt,
        nombreOlt: h.nombre_olt,
        archivo: h.paso === 'EXCEL' ? h.etiqueta : null,
        desde: h.fecha_creacion,
        hasta: h.fecha_creacion,
        usuario: h.us_creacion,
        duracionTotalMs: h.duracion_ms ?? 0,
        conFallo: !h.ok,
        entradas: [h],
      });
      continue;
    }
    lote.entradas.push(h);
    lote.duracionTotalMs += h.duracion_ms ?? 0;
    lote.conFallo = lote.conFallo || !h.ok;
    // La lista viene de más nueva a más vieja.
    if (h.fecha_creacion < lote.desde) lote.desde = h.fecha_creacion;
    if (h.fecha_creacion > lote.hasta) lote.hasta = h.fecha_creacion;
    lote.ipOlt = lote.ipOlt ?? h.ip_olt;
    lote.nombreOlt = lote.nombreOlt ?? h.nombre_olt;
    lote.usuario = lote.usuario ?? h.us_creacion;
    if (h.paso === 'EXCEL' && !lote.archivo) lote.archivo = h.etiqueta;
  }
  return Array.from(mapa.values()).sort((a, b) => (a.hasta < b.hasta ? 1 : -1));
};
/**
 * Resumen de una corrida: en qué quedó, sin tener que abrirla.
 *
 * Reemplaza la tira de cuadritos (uno por registro) que había antes: una
 * corrida de 40 clientes pintaba 40 cuadros "M" seguidos y no se leía nada. Lo
 * que hace falta saber de un vistazo es cuántos quedaron bien y cuántos hay
 * que atender, y eso son cuatro números.
 */
interface ResumenCorrida {
  /** Pasos de preparación que se corrieron, en orden y sin repetir. */
  preparacion: { paso: IMigracionOltPasoHistorial; ok: boolean }[];
  /** Clientes por estado final, contados UNA vez (manda el último intento). */
  migrados: number;
  acsPendiente: number;
  sinServicio: number;
  rechazados: number;
  noDesconfigurados: number;
  revisar: number;
  totalClientes: number;
}

/** Los pasos previos a la migración: son de consulta y van una vez. */
const PASOS_PREPARACION: IMigracionOltPasoHistorial[] = [
  'CONSULTAR_ONT',
  'CONSULTAR_SOFTNET',
  'SERVICE_PROFILE',
  'EXCEL',
  'CONFIGURACION_ONT',
  'PREPARAR',
];

/** Etiqueta corta del paso para el resumen. */
const PASO_CORTO: Record<string, string> = {
  CONSULTAR_ONT: '1 ONT',
  CONSULTAR_SOFTNET: '2 Softnet',
  SERVICE_PROFILE: '3 Perfiles',
  EXCEL: 'Excel',
  CONFIGURACION_ONT: 'Config ONT',
  PREPARAR: '4 Revisión',
};

/**
 * Cuenta el resultado de una corrida.
 *
 * Los clientes se cuentan por serie y no por registro: uno que falló y se
 * reintentó deja DOS entradas (MIGRAR y REINTENTO_ACTIVACION), y contarlas por
 * separado lo dejaría a la vez como "sin servicio" y como "migrado". Vale el
 * último intento, que es el estado en que quedó.
 */
const resumirCorrida = (entradas: IMigracionOltHistorialItem[]): ResumenCorrida => {
  const preparacion: { paso: IMigracionOltPasoHistorial; ok: boolean }[] = [];
  for (const paso of PASOS_PREPARACION) {
    const delPaso = entradas.filter((h) => h.paso === paso);
    if (delPaso.length > 0) {
      preparacion.push({ paso, ok: delPaso.every((h) => h.ok) });
    }
  }

  // Último intento por serie: la etiqueta de MIGRAR/REINTENTO es la serie.
  const ultimo = new Map<string, IMigracionOltHistorialItem>();
  for (const h of entradas) {
    if (h.paso !== 'MIGRAR' && h.paso !== 'REINTENTO_ACTIVACION') continue;
    const clave = h.etiqueta ?? h.id_historial;
    const previo = ultimo.get(clave);
    if (!previo || previo.fecha_creacion < h.fecha_creacion) ultimo.set(clave, h);
  }

  const out: ResumenCorrida = {
    preparacion,
    migrados: 0,
    acsPendiente: 0,
    sinServicio: 0,
    rechazados: 0,
    noDesconfigurados: 0,
    revisar: 0,
    totalClientes: ultimo.size,
  };
  for (const h of ultimo.values()) {
    const estado = String(h.resumen?.estado_final ?? '');
    switch (estado) {
      case 'COMPLETADO':
        out.migrados += 1;
        break;
      case 'COMPLETADO_AVISO':
        // Activó: lo único pendiente es que el ACS tome la configuración.
        if (h.resumen?.activar_ok === false) out.revisar += 1;
        else out.acsPendiente += 1;
        break;
      case 'FALLO_ACTIVAR':
        out.sinServicio += 1;
        break;
      case 'FALLO_CANCELAR':
        out.noDesconfigurados += 1;
        break;
      case 'RECHAZADO':
        out.rechazados += 1;
        break;
      default:
        out.revisar += 1;
    }
  }
  return out;
};

/**
 * El mismo resumen, pero contado desde las ejecuciones de la base.
 *
 * En el detalle de una corrida se usa esto y no las entradas del historial: la
 * base tiene una fila por cliente, ya consolidada con sus reintentos, así que
 * no hay que deducir nada.
 */
const resumirEjecuciones = (
  preparacion: ResumenCorrida['preparacion'],
  ejecuciones: IMigracionOltResultadoMigracion[],
): ResumenCorrida => {
  const out: ResumenCorrida = {
    preparacion,
    migrados: 0,
    acsPendiente: 0,
    sinServicio: 0,
    rechazados: 0,
    noDesconfigurados: 0,
    revisar: 0,
    totalClientes: ejecuciones.length,
  };
  for (const e of ejecuciones) {
    switch (e.estado_final) {
      case 'COMPLETADO':
        out.migrados += 1;
        break;
      case 'COMPLETADO_AVISO':
        if (e.activar_ok === false) out.revisar += 1;
        else out.acsPendiente += 1;
        break;
      case 'FALLO_ACTIVAR':
        out.sinServicio += 1;
        break;
      case 'FALLO_CANCELAR':
        out.noDesconfigurados += 1;
        break;
      case 'RECHAZADO':
        out.rechazados += 1;
        break;
      default:
        out.revisar += 1;
    }
  }
  return out;
};

/** Chip de contador: se omite si vale cero, salvo el de migrados. */
const ChipConteo: React.FC<{
  n: number;
  label: string;
  color: 'success' | 'error' | 'warning' | 'default';
  ayuda: string;
  visto?: boolean;
  siempre?: boolean;
}> = ({ n, label, color, ayuda, visto = false, siempre = false }) =>
  n === 0 && !siempre ? null : (
    <Tooltip arrow title={ayuda}>
      <Chip
        size="small"
        color={color}
        variant={color === 'default' ? 'outlined' : 'filled'}
        icon={visto ? <CheckCircleIcon /> : undefined}
        label={`${n} ${label}`}
      />
    </Tooltip>
  );

/** El resumen pintado: pasos corridos y en qué quedaron los clientes. */
const ResumenCorridaChips: React.FC<{ resumen: ResumenCorrida; compacto?: boolean }> = ({
  resumen,
  compacto = false,
}) => {
  const todoBien =
    resumen.sinServicio === 0 && resumen.revisar === 0 && resumen.noDesconfigurados === 0;
  return (
    <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
      {/* Los pasos de preparación: qué se alcanzó a correr. */}
      {resumen.preparacion.map((pp) => (
        <Tooltip key={pp.paso} arrow title={PASO_CFG[pp.paso]?.detalle ?? pp.paso}>
          <Chip
            size="small"
            variant="outlined"
            color={pp.ok ? 'default' : 'error'}
            label={PASO_CORTO[pp.paso] ?? pp.paso}
          />
        </Tooltip>
      ))}

      {resumen.totalClientes > 0 && (
        <>
          {!compacto && (
            <Typography variant="caption" color="text.secondary" sx={{ mx: 0.5 }}>
              &rarr;
            </Typography>
          )}
          <ChipConteo
            n={resumen.migrados}
            label="migrados"
            color="success"
            visto
            siempre
            ayuda="Se desconfiguró y se volvió a aprovisionar sin novedades."
          />
          <ChipConteo
            n={resumen.acsPendiente}
            label="con ACS pendiente"
            color="success"
            visto
            ayuda="Migrados: el servicio quedó activo. Falta que el ACS tome la configuración del ONT (hasta 20 min, se resuelve solo)."
          />
          <ChipConteo
            n={resumen.sinServicio}
            label="sin servicio"
            color="error"
            ayuda="Se desconfiguró y la activación no entró: hay que reintentar."
          />
          <ChipConteo
            n={resumen.revisar}
            label="a revisar"
            color="warning"
            ayuda="Respondió 200 pero el mensaje no confirma la activación."
          />
          <ChipConteo
            n={resumen.noDesconfigurados}
            label="no se desconfiguraron"
            color="warning"
            ayuda="No se pudo desconfigurar tras 3 intentos. El cliente siguió como estaba: nadie quedó caído."
          />
          <ChipConteo
            n={resumen.rechazados}
            label="rechazados"
            color="default"
            ayuda="No pasaron la validación, así que no se tocó la OLT. La consulta sí dejó guardada su configuración."
          />
          {todoBien && !compacto && (
            <Tooltip arrow title="Ningún cliente quedó sin servicio ni pendiente de revisión">
              <Chip
                size="small"
                color="success"
                variant="outlined"
                icon={<CheckCircleIcon />}
                label="validado"
              />
            </Tooltip>
          )}
        </>
      )}
    </Stack>
  );
};

/**
 * Una corrida en la lista: una línea que dice qué se hizo y cuándo.
 *
 * Es solo el índice. Al hacer clic se abre el detalle completo, en vez de
 * expandirse acá: con varias corridas abiertas a la vez la pantalla era un
 * muro de tarjetas y no se distinguía una de otra.
 */
const FilaCorrida: React.FC<{ lote: Lote; onAbrir: (l: Lote) => void }> = ({
  lote,
  onAbrir,
}) => {
  const resumen = resumirCorrida(lote.entradas);
  // Lo que necesita atención se ve en los chips del resumen; ya no se marca
  // además con un borde de color.
  return (
  <Paper
    variant="outlined"
    sx={{
      mb: 1,
      cursor: 'pointer',
      '&:hover': { bgcolor: 'action.hover' },
    }}
    onClick={() => onAbrir(lote)}
  >
    <Stack direction="row" spacing={1.25} alignItems="center" sx={{ px: 1.5, py: 1 }}>
      <Typography variant="body2" sx={{ minWidth: 58, fontWeight: 700 }}>
        {hora(lote.desde)}
      </Typography>
      <Typography variant="body2" fontWeight={700} sx={{ minWidth: 112 }}>
        {lote.ipOlt ?? '—'}
      </Typography>
      {lote.nombreOlt && lote.nombreOlt !== 'null' ? (
        <Chip size="small" variant="outlined" label={lote.nombreOlt} />
      ) : null}
      {lote.archivo && (
        <Tooltip arrow title="Excel cargado en esta corrida; el archivo no se guarda">
          <Chip size="small" variant="outlined" label={lote.archivo} />
        </Tooltip>
      )}
      <ResumenCorridaChips resumen={resumen} compacto />
      <Box sx={{ flexGrow: 1 }} />
      <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
        {lote.entradas.length} registro(s) · {seg(lote.duracionTotalMs)}
        {lote.usuario ? ` · ${lote.usuario}` : ''}
      </Typography>
      <ChevronRightIcon fontSize="small" color="action" />
    </Stack>
  </Paper>
  );
};

/**
 * Detalle de UNA corrida: qué se consultó y qué pasó con cada cliente.
 *
 * Trae las ejecuciones de esa corrida aparte del historial, porque son dos
 * cosas distintas: el historial guarda los PASOS (consultas) y la bitácora
 * guarda los CLIENTES migrados. Acá se ven juntas, que es lo que uno quiere al
 * auditar "qué hice en esta corrida".
 */
const DetalleCorrida: React.FC<{
  lote: Lote;
  puedeEjecutar: boolean;
  onVolver: () => void;
}> = ({ lote, puedeEjecutar, onVolver }) => {
  const { data, isFetching, refetch } = useMigracionOltEjecuciones({
    corrida_id: lote.corridaId ?? '',
    enabled: !!lote.corridaId,
  });
  const [serieReintentando, setSerieReintentando] = useState<string | null>(null);
  const [enLote, setEnLote] = useState(false);
  const [progreso, setProgreso] = useState<{ hechas: number; total: number; ok: number } | null>(
    null,
  );
  const detenerRef = useRef(false);
  const reintentar = useMigracionOltReintentarActivacion();

  const res = (data as { data?: {
    resultados?: IMigracionOltResultadoMigracion[];
    completados?: number;
    acs_pendiente?: number;
    sin_servicio?: number;
    rechazados?: number;
  } } | undefined)?.data;
  const ejecuciones = res?.resultados ?? [];
  const sinServicio = ejecuciones.filter((e) => e.estado_final === 'FALLO_ACTIVAR');
  // Los pasos salen del historial; los clientes, de las ejecuciones.
  const resumen = resumirEjecuciones(resumirCorrida(lote.entradas).preparacion, ejecuciones);

  const handleReintentar = async (item: IMigracionOltResultadoMigracion) => {
    if (!item.id_ejecucion) return;
    setSerieReintentando(item.serie);
    try {
      await reintentar.mutateAsync({
        id_ejecucion: item.id_ejecucion,
        corrida_id: item.corrida_id ?? undefined,
      });
    } finally {
      setSerieReintentando(null);
      refetch();
    }
  };

  /**
   * Reintenta todos los sin servicio de ESTA corrida, uno por uno.
   * Secuencial porque la OLT atiende una sesión SSH a la vez.
   */
  const handleReintentarTodas = async () => {
    const lista = sinServicio.filter((p) => !!p.id_ejecucion);
    if (lista.length === 0) return;
    detenerRef.current = false;
    setEnLote(true);
    setProgreso({ hechas: 0, total: lista.length, ok: 0 });
    try {
      for (let i = 0; i < lista.length; i += 1) {
        if (detenerRef.current) break;
        const item = lista[i];
        setSerieReintentando(item.serie);
        try {
          const r: any = await reintentar.mutateAsync({
            id_ejecucion: item.id_ejecucion as string,
            corrida_id: item.corrida_id ?? undefined,
          });
          const logrado = r?.data?.estado_final === 'COMPLETADO';
          setProgreso((p) => (p ? { ...p, hechas: i + 1, ok: p.ok + (logrado ? 1 : 0) } : p));
        } catch {
          setProgreso((p) => (p ? { ...p, hechas: i + 1 } : p));
        }
      }
    } finally {
      setSerieReintentando(null);
      setEnLote(false);
      refetch();
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
        <Button
          size="small"
          startIcon={<ArrowBackIcon />}
          onClick={onVolver}
          sx={{ textTransform: 'none', minHeight: 44 }}
        >
          Volver a las corridas
        </Button>
      </Stack>

      {/* Encabezado: qué se hizo, cuándo y sobre qué OLT. */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="body1" fontWeight={700} sx={{ mb: 0.5 }}>
          {etiquetaDia(lote.hasta)} a las {hora(lote.desde)} · migración de la OLT{' '}
          {lote.ipOlt ?? '—'}
          {lote.nombreOlt && lote.nombreOlt !== 'null' ? ` · ${lote.nombreOlt}` : ''}
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
          <Chip size="small" variant="outlined" label={`duración ${seg(lote.duracionTotalMs)}`} />
          {lote.archivo && <Chip size="small" variant="outlined" label={lote.archivo} />}
          {lote.usuario && <Chip size="small" variant="outlined" label={lote.usuario} />}
        </Stack>

        {/* Resumen de la corrida: los pasos que se corrieron y en qué quedó
            cada cliente. Es lo primero que se mira al auditar. */}
        <Box sx={{ mt: 1.5 }}>
          <ResumenCorridaChips resumen={resumen} />
        </Box>
        {!lote.corridaId && (
          <Alert severity="info" variant="outlined" sx={{ mt: 1.5, py: 0.5 }}>
            Esta entrada es anterior al registro por corrida, así que no tiene clientes
            asociados. Solo se ven sus pasos.
          </Alert>
        )}
      </Paper>

      {/* Reintento en lote, acotado a esta corrida. */}
      {puedeEjecutar && sinServicio.length > 0 && (
        <Paper variant="outlined" sx={{ p: 1.5, mb: 2, borderColor: 'error.main' }}>
          <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
            <Button
              variant="contained"
              color="error"
              disableElevation
              size="small"
              disabled={enLote}
              startIcon={enLote ? <CircularProgress size={16} color="inherit" /> : <RouterIcon />}
              onClick={handleReintentarTodas}
              sx={{ textTransform: 'none', fontWeight: 600, minHeight: 44 }}
            >
              {enLote ? 'Reintentando...' : `Reintentar los ${sinServicio.length} sin servicio`}
            </Button>
            {enLote && (
              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  detenerRef.current = true;
                }}
                sx={{ textTransform: 'none', minHeight: 44 }}
              >
                Detener
              </Button>
            )}
            {progreso && (
              <Box sx={{ flexGrow: 1, minWidth: 220 }}>
                <LinearProgress
                  variant="determinate"
                  value={progreso.total ? (progreso.hechas / progreso.total) * 100 : 0}
                  sx={{ height: 8, borderRadius: 1, mb: 0.5 }}
                />
                <Typography variant="caption" color="text.secondary">
                  {progreso.hechas} de {progreso.total} · <strong>{progreso.ok}</strong>{' '}
                  activado(s)
                  {serieReintentando ? ` · en curso ${serieReintentando}` : ''}
                  {enLote ? '. No cierres esta ventana.' : '. Terminado.'}
                </Typography>
              </Box>
            )}
          </Stack>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
            Repite <strong>solo la activación</strong>; no vuelve a desconfigurar nada. Va de a
            uno: la OLT atiende una sesión SSH a la vez.
          </Typography>
        </Paper>
      )}

      {/* Los clientes de la corrida. */}
      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
        Clientes {isFetching ? '(cargando...)' : `(${ejecuciones.length})`}
      </Typography>
      {!isFetching && ejecuciones.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ pb: 2 }}>
          Esta corrida no migró clientes: solo se hicieron consultas.
        </Typography>
      ) : (
        ejecuciones.map((e) => (
          <BitacoraCliente
            key={e.id_ejecucion ?? e.serie}
            item={e}
            onReintentar={
              puedeEjecutar && e.estado_final === 'FALLO_ACTIVAR' ? handleReintentar : undefined
            }
            reintentando={serieReintentando === e.serie}
          />
        ))
      )}

      {/* Los pasos de consulta de la corrida. */}
      <Typography variant="subtitle2" fontWeight={700} sx={{ mt: 3, mb: 1 }}>
        Pasos ejecutados ({lote.entradas.length})
      </Typography>
      <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
        {[...lote.entradas]
          .sort((a, b) => (a.fecha_creacion < b.fecha_creacion ? -1 : 1))
          .map((h) => (
            <Entrada key={h.id_historial} item={h} />
          ))}
      </Paper>
    </Box>
  );
};

/** Etiqueta del día para la cabecera de grupo: Hoy / Ayer / fecha. */
const etiquetaDia = (iso: string) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const hoy = new Date();
  const ayer = new Date();
  ayer.setDate(hoy.getDate() - 1);
  const mismoDia = (a: Date, b: Date) => a.toDateString() === b.toDateString();
  if (mismoDia(d, hoy)) return 'Hoy';
  if (mismoDia(d, ayer)) return 'Ayer';
  return d.toLocaleDateString('es-EC', { day: 'numeric', month: 'long', year: 'numeric' });
};
/**
 * Lo que quedó esperando algo de corridas anteriores.
 *
 * Vive en el Historial y no en el tab de Migración a propósito: ese tab es solo
 * para la corrida del momento. Acá se retoma lo de antes.
 *
 * Dos grupos con significados muy distintos:
 *   sin servicio  se desconfiguró y la activación no entró -> hay que reintentar
 *   ACS pendiente el servicio está arriba, falta verificar el ACS -> no se toca
 */
const PendientesAnteriores: React.FC<{ puedeEjecutar: boolean }> = ({ puedeEjecutar }) => {
  const { data, isFetching, refetch } = useMigracionOltPendientes({ enabled: puedeEjecutar });
  const [serieReintentando, setSerieReintentando] = useState<string | null>(null);
  /** Progreso del reintento en lote: {hechas, total, ok}. */
  const [enLote, setEnLote] = useState(false);
  const [progresoLote, setProgresoLote] = useState<{
    hechas: number;
    total: number;
    ok: number;
  } | null>(null);
  /** Ref y no estado: el bucle necesita leer el valor actual, no el capturado. */
  const detenerRef = useRef(false);
  const reintentar = useMigracionOltReintentarActivacion();

  const pendientes =
    (data as { data?: { resultados?: IMigracionOltResultadoMigracion[] } } | undefined)?.data
      ?.resultados ?? [];
  const sinServicio = pendientes.filter((p) => p.estado_final === 'FALLO_ACTIVAR');
  const acsPendiente = pendientes.filter((p) => p.estado_final !== 'FALLO_ACTIVAR');
  const [grupo, setGrupo] = useState<'sin_servicio' | 'acs'>('sin_servicio');

  const handleReintentar = async (item: IMigracionOltResultadoMigracion) => {
    if (!item.id_ejecucion) return;
    setSerieReintentando(item.serie);
    try {
      await reintentar.mutateAsync({
        id_ejecucion: item.id_ejecucion,
        corrida_id: item.corrida_id ?? undefined,
      });
    } finally {
      setSerieReintentando(null);
      // El reintento acumula sobre la misma fila, así que basta releer.
      refetch();
    }
  };

  /**
   * Reintenta TODOS los que quedaron sin servicio, uno por uno.
   *
   * Secuencial a propósito: la OLT atiende una sesión SSH a la vez, así que
   * dispararlos en paralelo no acelera nada y puede dejar el equipo a medias.
   * Se puede detener: termina el que esté en curso y para.
   *
   * No se recarga la lista entre cliente y cliente —solo al final— para que no
   * se reordene bajo el cursor mientras avanza.
   */
  const handleReintentarTodas = async () => {
    const aReintentar = sinServicio.filter((p) => !!p.id_ejecucion);
    if (aReintentar.length === 0) return;

    detenerRef.current = false;
    setEnLote(true);
    setProgresoLote({ hechas: 0, total: aReintentar.length, ok: 0 });
    try {
      for (let i = 0; i < aReintentar.length; i += 1) {
        if (detenerRef.current) break;
        const item = aReintentar[i];
        setSerieReintentando(item.serie);
        try {
          const res: any = await reintentar.mutateAsync({
            id_ejecucion: item.id_ejecucion as string,
            corrida_id: item.corrida_id ?? undefined,
          });
          const logrado = res?.data?.estado_final === 'COMPLETADO';
          setProgresoLote((p) =>
            p ? { ...p, hechas: i + 1, ok: p.ok + (logrado ? 1 : 0) } : p,
          );
        } catch {
          // El hook ya reportó el error; se sigue con el siguiente para no
          // dejar al resto sin intentar por culpa de uno.
          setProgresoLote((p) => (p ? { ...p, hechas: i + 1 } : p));
        }
      }
    } finally {
      setSerieReintentando(null);
      setEnLote(false);
      refetch();
    }
  };

  if (!isFetching && pendientes.length === 0) return null;

  const visibles = grupo === 'sin_servicio' ? sinServicio : acsPendiente;

  return (
    <Box sx={{ mb: 3 }}>
      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
        <Typography fontWeight={600}>Pendientes de corridas anteriores</Typography>
        <Box sx={{ flexGrow: 1 }} />
        <ToggleButtonGroup
          size="small"
          exclusive
          value={grupo}
          onChange={(_, v) => v && setGrupo(v)}
        >
          <ToggleButton value="sin_servicio">Sin servicio ({sinServicio.length})</ToggleButton>
          <ToggleButton value="acs">ACS pendiente ({acsPendiente.length})</ToggleButton>
        </ToggleButtonGroup>
        <Tooltip arrow title="Recargar">
          <IconButton onClick={() => refetch()} disabled={isFetching} sx={{ minWidth: 44 }}>
            {isFetching ? <CircularProgress size={20} /> : <RefreshIcon />}
          </IconButton>
        </Tooltip>
      </Stack>

      <Alert
        severity={grupo === 'sin_servicio' ? 'error' : 'info'}
        variant="outlined"
        sx={{ mb: 2, py: 0.5 }}
      >
        {grupo === 'sin_servicio' ? (
          <>
            Se desconfiguró la ONT y la activación no entró. El reintento repite{' '}
            <strong>solo la activación</strong>; no vuelve a desconfigurar nada, y el resultado se
            acumula sobre esta misma entrada.
          </>
        ) : (
          <>
            El servicio <strong>está arriba</strong>. Lo que falta es verificar en el ACS que la
            configuración se aplicó (hasta 20 min). No hay nada que reintentar acá.
          </>
        )}
      </Alert>

      {/* Reintento en lote: solo tiene sentido para los que quedaron sin
          servicio. Va de a uno (la OLT atiende una sesión SSH a la vez) y se
          puede detener a mitad de camino. */}
      {grupo === 'sin_servicio' && puedeEjecutar && sinServicio.length > 0 && (
        <Paper variant="outlined" sx={{ p: 1.5, mb: 2, borderColor: 'error.main' }}>
          <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
            <Button
              variant="contained"
              color="error"
              disableElevation
              size="small"
              disabled={enLote}
              startIcon={
                enLote ? <CircularProgress size={16} color="inherit" /> : <RouterIcon />
              }
              onClick={handleReintentarTodas}
              sx={{ textTransform: 'none', fontWeight: 600, minHeight: 44 }}
            >
              {enLote
                ? 'Reintentando...'
                : `Reintentar las ${sinServicio.length} sin servicio`}
            </Button>

            {enLote && (
              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  detenerRef.current = true;
                }}
                sx={{ textTransform: 'none', minHeight: 44 }}
              >
                Detener
              </Button>
            )}

            {progresoLote && (
              <Box sx={{ flexGrow: 1, minWidth: 220 }}>
                <LinearProgress
                  variant="determinate"
                  value={
                    progresoLote.total
                      ? (progresoLote.hechas / progresoLote.total) * 100
                      : 0
                  }
                  sx={{ height: 8, borderRadius: 1, mb: 0.5 }}
                />
                <Typography variant="caption" color="text.secondary">
                  {progresoLote.hechas} de {progresoLote.total} ·{' '}
                  <strong>{progresoLote.ok}</strong> activado(s)
                  {serieReintentando ? ` · en curso ${serieReintentando}` : ''}
                  {enLote ? '. No cierres esta ventana.' : '. Terminado.'}
                </Typography>
              </Box>
            )}
          </Stack>

          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
            Va de a uno: la OLT atiende una sesión SSH a la vez, así que lanzarlos en
            paralelo no acelera y puede dejar el equipo a medias. Cada reintento repite{' '}
            <strong>solo la activación</strong> y se acumula sobre su propia entrada.
          </Typography>
        </Paper>
      )}

      {visibles.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ pb: 2 }}>
          Nada en este grupo.
        </Typography>
      ) : (
        visibles.map((p) => (
          <BitacoraCliente
            key={p.id_ejecucion ?? p.serie}
            item={p}
            onReintentar={
              puedeEjecutar && p.estado_final === 'FALLO_ACTIVAR' ? handleReintentar : undefined
            }
            reintentando={serieReintentando === p.serie}
          />
        ))
      )}

      <Divider sx={{ mt: 1 }} />
    </Box>
  );
};


interface Props {
  /** El usuario puede ejecutar: sin esto no hay botón de reintentar. */
  puedeEjecutar?: boolean;
  /** IP de la OLT del tab, para ofrecer el filtro ya cargado. */
  ipOltActual?: string;
}

const MigracionOltHistorial: React.FC<Props> = ({ ipOltActual = '', puedeEjecutar = false }) => {
  /** Corrida abierta. Null = se ve la lista. */
  const [corridaSel, setCorridaSel] = useState<Lote | null>(null);
  const [soloEstaOlt, setSoloEstaOlt] = useState(false);
  const [limite, setLimite] = useState(50);
  const [paso, setPaso] = useState<'todos' | IMigracionOltPasoHistorial>('todos');

  const { data, isFetching, refetch } = useMigracionOltHistorial({
    ip_olt: soloEstaOlt ? ipOltActual : '',
    limite,
  });

  const historial = (data as { data?: { resultados?: IMigracionOltHistorialItem[] } } | undefined)
    ?.data;
  const todas = historial?.resultados ?? [];
  const lotes = useMemo(() => agruparEnLotes(todas), [todas]);
  /** Lotes agrupados por día, de más nuevo a más viejo. */
  const porDia = useMemo(() => {
    const mapa = new Map<string, Lote[]>();
    for (const l of lotes) {
      const d = etiquetaDia(l.hasta);
      if (!mapa.has(d)) mapa.set(d, []);
      mapa.get(d)!.push(l);
    }
    return Array.from(mapa.entries());
  }, [lotes]);
  const visibles = paso === "todos" ? todas : todas.filter((h) => h.paso === paso);

  /** Solo se ofrecen los filtros de pasos que realmente aparecen. */
  const pasosPresentes = Array.from(new Set(todas.map((h) => h.paso)));

  // Maestro-detalle: al abrir una corrida se ve SOLO esa, con sus clientes y
  // sus pasos. Antes todas se expandian en la misma pantalla y no se
  // distinguia una corrida de otra.
  if (corridaSel) {
    return (
      <DetalleCorrida
        lote={corridaSel}
        puedeEjecutar={puedeEjecutar}
        onVolver={() => setCorridaSel(null)}
      />
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Lo pendiente de antes va primero: es lo único accionable de esta
          pantalla. El tab de Migración quedó solo para la corrida del momento. */}
      {/* La lista de corridas es lo principal. Los pendientes globales quedan
          abajo, en un acordeon, como red de seguridad para ejecuciones viejas
          que no tienen corrida asociada. */}

      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
        <Typography fontWeight={600}>Historial de consultas</Typography>
        <Box sx={{ flexGrow: 1 }} />

        {ipOltActual && (
          <ToggleButtonGroup
            size="small"
            exclusive
            value={soloEstaOlt ? 'olt' : 'todas'}
            onChange={(_, v) => v && setSoloEstaOlt(v === 'olt')}
          >
            <ToggleButton value="todas">Todas las OLT</ToggleButton>
            <ToggleButton value="olt">Solo {ipOltActual}</ToggleButton>
          </ToggleButtonGroup>
        )}

        <TextField
          select
          size="small"
          label="Mostrar"
          value={limite}
          onChange={(e) => setLimite(Number(e.target.value))}
          sx={{ minWidth: 130 }}
        >
          {[25, 50, 100, 200].map((n) => (
            <MenuItem key={n} value={n}>
              Últimas {n}
            </MenuItem>
          ))}
        </TextField>

        <Tooltip arrow title="Recargar">
          <IconButton onClick={() => refetch()} disabled={isFetching} sx={{ minWidth: 44 }}>
            {isFetching ? <CircularProgress size={20} /> : <RefreshIcon />}
          </IconButton>
        </Tooltip>
      </Stack>

      {pasosPresentes.length > 1 && (
        <ToggleButtonGroup
          size="small"
          exclusive
          value={paso}
          onChange={(_, v) => v && setPaso(v)}
          sx={{ mb: 2, flexWrap: 'wrap' }}
        >
          <ToggleButton value="todos">Todos ({todas.length})</ToggleButton>
          {pasosPresentes.map((p) => (
            <ToggleButton key={p} value={p}>
              {PASO_CFG[p]?.label ?? p} ({todas.filter((h) => h.paso === p).length})
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      )}

      {isFetching && todas.length === 0 && (
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 4 }}>
          <CircularProgress size={20} />
          <Typography variant="body2" color="text.secondary">
            Cargando historial...
          </Typography>
        </Stack>
      )}

      {!isFetching && visibles.length === 0 && (
        <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ py: 4 }}>
          Todavía no hay consultas registradas. Ejecuta cualquiera de los pasos y aparecerán aquí
          con su respuesta completa y el tiempo que tardaron.
        </Typography>
      )}

      {/* Agrupado por día y, dentro, por corrida. Lo que se revisa es "qué se
          hizo sobre esta OLT con este archivo", no una lista plana de pasos. */}
      {porDia.map(([dia, delDia], iDia) => (
        <Box key={dia} sx={{ mb: 2.5 }}>
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              mb: 0.75,
              fontWeight: 700,
              letterSpacing: 0.6,
              textTransform: 'uppercase',
              color: 'text.secondary',
            }}
          >
            {dia} · {delDia.length} corrida(s)
          </Typography>
          {delDia
            .filter(
              (l) => paso === 'todos' || l.entradas.some((h) => h.paso === paso),
            )
            .map((l) => (
              <FilaCorrida key={l.clave} lote={l} onAbrir={setCorridaSel} />
            ))}
        </Box>
      ))}

      {historial && historial.resultados && historial.resultados.length >= limite && (
        <Alert severity="info" variant="outlined" sx={{ mt: 1, py: 0.5 }}>
          Se muestran las últimas {limite} entradas. Cada una guarda la respuesta cruda completa,
          así que el listado va paginado a propósito.
        </Alert>
      )}

      {/* Red de seguridad: ejecuciones pendientes de CUALQUIER corrida,
          incluidas las viejas que no tienen corrida_id y por eso no aparecen
          en la lista de arriba. Colapsado para no competir con lo principal. */}
      <Accordion variant="outlined" sx={{ mt: 3 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="body2" fontWeight={600}>
            Pendientes de todas las corridas
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 0 }}>
          <PendientesAnteriores puedeEjecutar={puedeEjecutar} />
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default MigracionOltHistorial;
